import React from 'react';
import { ShieldAlert, ExternalLink, FileCheck, Scale, AlertTriangle, CheckCircle, Info } from 'lucide-react';

export default function ComplianceCenter() {
  return (
    <div className="space-y-8 pb-10 animate-in fade-in">
      
      <div className="bg-gradient-to-r from-slate-900 to-slate-800 rounded-3xl p-8 md:p-10 text-white shadow-xl relative overflow-hidden">
        <div className="absolute top-0 right-0 w-64 h-64 bg-orange-500/10 rounded-full mix-blend-screen filter blur-[50px] pointer-events-none"></div>
        <div className="relative z-10">
          <div className="flex items-center gap-3 mb-4">
            <Scale className="text-orange-400 w-8 h-8" />
            <h2 className="text-3xl font-black tracking-tight">Centre de Conformité</h2>
          </div>
          <p className="text-slate-300 max-w-2xl text-sm md:text-base leading-relaxed font-medium">
            Forfeo vous accompagne dans le respect de la <strong className="text-white">Loi 25</strong> (protection des renseignements personnels) et des règles du <strong className="text-white">Tribunal administratif du logement (TAL)</strong>. 
          </p>
        </div>
      </div>

      <div className="bg-orange-50 border-l-4 border-orange-500 p-6 rounded-r-2xl flex gap-4 shadow-sm items-start">
        <AlertTriangle className="text-orange-600 shrink-0 w-6 h-6" />
        <div>
          <h3 className="font-black text-orange-900 text-lg mb-2">Rappel important : Numéro d'Assurance Sociale (NAS)</h3>
          <p className="text-orange-800 text-sm leading-relaxed font-medium">
            Selon la Commission d'accès à l'information du Québec, vous ne pouvez pas exiger le NAS d'un candidat locataire pour effectuer une vérification de crédit. Le locataire a le droit de le refuser. Forfeo a conçu son système de préqualification pour vérifier l'identité et le crédit <strong>sans exiger le NAS</strong>.
          </p>
        </div>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-6">
            <div className="bg-blue-50 p-3 rounded-xl"><FileCheck className="text-blue-600 w-6 h-6" /></div>
            <h3 className="text-xl font-black text-slate-900">Checklist du locateur</h3>
          </div>
          <ul className="space-y-5 text-sm font-medium">
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-slate-600"><strong className="text-slate-900 block mb-1">Demander le consentement explicite</strong> Toujours obtenir l'accord écrit avant de faire une enquête de crédit (Géré automatiquement par Forfeo).</span>
            </li>
            <li className="flex items-start gap-3">
              <CheckCircle className="text-green-500 w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-slate-600"><strong className="text-slate-900 block mb-1">Minimisation des données</strong> Ne demandez que les documents nécessaires pour prouver l'identité et la capacité de payer.</span>
            </li>
            <li className="flex items-start gap-3">
              <Info className="text-blue-400 w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-slate-600"><strong className="text-slate-900 block mb-1">Dépôt de garantie illégal</strong> Au Québec, il est interdit d'exiger un dépôt (ex: premier et dernier mois) autre que le premier mois de loyer.</span>
            </li>
            <li className="flex items-start gap-3">
              <Info className="text-blue-400 w-5 h-5 shrink-0 mt-0.5" />
              <span className="text-slate-600"><strong className="text-slate-900 block mb-1">Clause F du bail</strong> Si votre immeuble a 5 ans ou moins, n'oubliez pas de remplir la section F du bail.</span>
            </li>
          </ul>
        </div>

        <div className="bg-white rounded-3xl shadow-sm border border-slate-200 p-8">
          <div className="flex items-center gap-3 mb-6 border-b border-slate-100 pb-6">
            <div className="bg-slate-100 p-3 rounded-xl"><ShieldAlert className="text-slate-700 w-6 h-6" /></div>
            <h3 className="text-xl font-black text-slate-900">Ressources officielles TAL</h3>
          </div>
          
          <div className="space-y-4">
            {/* ✅ NOUVEAUX LIENS OFFICIELS DU GOUVERNEMENT DU QUÉBEC */}
            <a href="https://www.tal.gouv.qc.ca/fr/signature-d-un-bail/" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between p-5 bg-slate-50 hover:bg-teal-50 rounded-2xl border border-slate-100 hover:border-teal-200 transition-all">
              <span className="font-bold text-slate-700 group-hover:text-teal-700">Le bail et ses annexes</span>
              <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-teal-600" />
            </a>
            
            <a href="https://www.tal.gouv.qc.ca/fr/reconduction-du-bail-et-fixation-de-loyer/modification-d-une-condition-du-bail" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between p-5 bg-slate-50 hover:bg-teal-50 rounded-2xl border border-slate-100 hover:border-teal-200 transition-all">
              <span className="font-bold text-slate-700 group-hover:text-teal-700">Modification des conditions du bail</span>
              <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-teal-600" />
            </a>

            <a href="https://www.tal.gouv.qc.ca/fr/droit-du-locateur-de-mettre-fin-au-bail/eviction-pour-subdivision-agrandissement-substantiel-ou-changement-d-affection-d-un-logement" target="_blank" rel="noopener noreferrer" className="group flex items-center justify-between p-5 bg-slate-50 hover:bg-teal-50 rounded-2xl border border-slate-100 hover:border-teal-200 transition-all">
              <span className="font-bold text-slate-700 group-hover:text-teal-700">Règles d'éviction et de reprise</span>
              <ExternalLink className="w-5 h-5 text-slate-400 group-hover:text-teal-600" />
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
